import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import type {
  AffiliationManagerMappingDisplay,
  AffiliationManagerReassignmentDisplay,
  AffiliationMonthlyPlanDisplay,
  AffiliationProgramCreateDisplay,
  AffiliationProgramDisplay,
  AffiliationProgramInputs,
  AffiliationProgramUpdate,
  AffiliationSubjectDisplay,
  AffiliationSubjectsCreateDisplay,
  AffiliationSubjectsInputs,
  AffiliationSubjectUpdate,
  AcademicCalendarEventDisplay,
  ApplyAutoFillInputs,
  AcknowledgeAlertInputs,
  AdvanceAssessmentStatusInputs,
  AdvanceLifecycleStageInputs,
  AemDashboardSnapshotDisplay,
  AemTaskCountsDisplay,
  AemTaskDisplay,
  AssessmentDisplay,
  AssessmentSummaryDisplay,
  AutoFillPreviewDisplay,
  CancelAemTaskInputs,
  CloseTicketInputs,
  AddTicketMessageInputs,
  CompleteAemTaskInputs,
  CompleteChecklistItemInputs,
  ConfirmAcademicEventDateInputs,
  CreateAcademicCalendarInputs,
  UpdateAcademicCalendarInputs,
  CreateAemTaskInputs,
  CreateAssessmentInputs,
  CreateVisitInputs,
  DepartmentEscalationConfigDisplay,
  DepartmentEscalationConfigInputs,
  LegacyMigrationResultDisplay,
  NbaResultDisplay,
  OnboardingChecklistItemDisplay,
  PrincipalOnboardingView,
  ProgramSubjectTree,
  RateTicketInputs,
  ReassignAffiliationManagerInputs,
  ReassignTaskInputs,
  SaveMonthlyPlanInputs,
  EffectiveHolidayMonthDisplay,
  HolidayOverrideDisplay,
  SaveHolidayOverridesInputs,
  SchoolAlertDisplay,
  SchoolHealthScoreBrief,
  SchoolHealthScoreDisplay,
  SchoolLifecycleStageDisplay,
  SchoolTimelineEventDisplay,
  SchoolTicketDisplay,
  SchoolTicketInputs,
  SchoolTicketSummaryDisplay,
  ShareVisitMomInputs,
  SnoozeAemTaskInputs,
  TicketCountsDisplay,
  UpdateClassroomObservationInputs,
  UpdateInfraCheckInputs,
  UpdateKdmClosingInputs,
  UpdateProgramPlanUrlsInputs,
  UpdateStudentInteractionInputs,
  UpdateTeacherMeetingInputs,
  UpdateTicketStatusInputs,
  UpdateVisitPreBriefInputs,
  UpdateVisitStatusInputs,
  UpdateWeakChaptersInputs,
  VisitDisplay,
  VisitPreBriefDataDisplay,
  VisitTravelClusterDisplay,
  AemProgramBatchDisplay,
  AemProgramBatchInputs,
  AemProgramBatchUpdate,
  LegacyQueryDisplay,
  ProgramPlanFileUploadInput,
} from "@/types/aem";
import type { AcademicPlanFileUploadUrl } from "@/types/admin";
import type { ApiResponse, AzureUploadUrl, IdNamePair, PaginatedResponse } from "@/types/global";

export default class AemApi extends ApiVerified {
  constructor(apiBaseUrl: string) {
    super(apiBaseUrl);
  }

  // ─── plan-§5.A–§5.I-D2 — Program & Subject Setup ─────────────────────────

  async createAffiliationProgram(inputs: AffiliationProgramInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AffiliationProgramCreateDisplay>(
        "affiliation-ops/create-affiliation-program",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Course-subject options for a course (Id = CourseSubjectId) — source for the
  // required CourseSubjectIds on program create / add-subjects. Path verified against the backend
  // router (affiliation_ops.py → GET /courses/{courseId}/subject-options → list[IdNamePair]); it's
  // just missing from the checked-in docs/api/affiliation-ops.json (stale — regenerate that contract).
  async getCourseSubjectOptions(courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<IdNamePair[]>(
        `affiliation-ops/courses/${courseId}/subject-options`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateAffiliationProgram(inputs: AffiliationProgramUpdate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<AffiliationProgramDisplay>(
        "affiliation-ops/update-affiliation-program",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationPrograms(params: {
    affiliationId: number;
    academicSession?: number;
    includeInactive?: boolean;
  }): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AffiliationProgramDisplay[]>(
        "affiliation-ops/get-affiliation-programs",
        { params },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createAffiliationSubjects(inputs: AffiliationSubjectsInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AffiliationSubjectsCreateDisplay>(
        "affiliation-ops/create-affiliation-subjects",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateAffiliationSubject(inputs: AffiliationSubjectUpdate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<AffiliationSubjectDisplay>(
        "affiliation-ops/update-affiliation-subject",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSubjectsForProgram(programId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AffiliationSubjectDisplay[]>(
        "affiliation-ops/get-subjects-for-program",
        { params: { affiliationProgramId: programId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getProgramSubjectTree(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ProgramSubjectTree[]>(
        "affiliation-ops/get-program-subject-tree",
        { params: { affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Batch Management ────────────────────────────────────────────────────

  async createProgramBatch(inputs: AemProgramBatchInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AemProgramBatchDisplay>(
        "affiliation-ops/create-program-batch",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateProgramBatch(inputs: AemProgramBatchUpdate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<AemProgramBatchDisplay>(
        "affiliation-ops/update-program-batch",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getBatchesForProgram(programId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AemProgramBatchDisplay[]>(
        "affiliation-ops/get-batches-for-program",
        { params: { affiliationProgramId: programId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Classes available for a program — feeds the batch create/edit class picker (D4/A10).
  // Returns IdNamePair[] (Id + display name), mappable straight to InputOption[].
  async getClassesForProgram(programId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<IdNamePair[]>(
        "affiliation-ops/get-classes-for-program",
        { params: { affiliationProgramId: programId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getLegacyQueries(params: {
    affiliationId: number;
    pageNumber?: number;
    pageSize?: number;
  }): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<LegacyQueryDisplay>>(
        "affiliation-ops/get-legacy-queries",
        { params },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getProgramPlanUploadUrls(inputs: ProgramPlanFileUploadInput): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AcademicPlanFileUploadUrl[]>(
        "affiliation-ops/get-program-plan-upload-urls",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async reassignAffiliationManager(inputs: ReassignAffiliationManagerInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<AffiliationManagerReassignmentDisplay>(
        "affiliation-ops/reassign-affiliation-manager",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getManagerAssignmentHistory(params: {
    affiliationId: number;
    pageNumber?: number;
    pageSize?: number;
  }): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<
        PaginatedResponse<AffiliationManagerMappingDisplay>
      >("affiliation-ops/get-manager-assignment-history", { params });
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── plan-§5.J–§5.N — School Health ──────────────────────────────────────

  async getDashboardSnapshot(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AemDashboardSnapshotDisplay>(
        "aem-health/get-dashboard-snapshot",
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolHealthScore(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SchoolHealthScoreDisplay>(
        "aem-health/get-school-health-score",
        { params: { affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolHealthScoreHistory(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SchoolHealthScoreBrief[]>(
        "aem-health/get-school-health-score-history",
        { params: { affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolLifecycleStage(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SchoolLifecycleStageDisplay>(
        "aem-health/get-school-lifecycle-stage",
        { params: { affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async advanceSchoolLifecycleStage(inputs: AdvanceLifecycleStageInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<SchoolLifecycleStageDisplay>(
        "aem-health/advance-school-lifecycle-stage",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── plan-§5.O–§5.Q — Onboarding Checklist ───────────────────────────────

  async getOnboardingChecklist(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<OnboardingChecklistItemDisplay[]>(
        "aem-health/get-onboarding-checklist",
        { params: { affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async completeChecklistItem(inputs: CompleteChecklistItemInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<null>(
        "aem-health/complete-checklist-item",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPrincipalOnboardingView(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PrincipalOnboardingView>(
        "aem-health/get-principal-onboarding-view",
        { params: { affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── plan-§5.R–§5.U — NBA / Timeline / Alerts ────────────────────────────

  async getNextBestActions(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<NbaResultDisplay>(
        "aem-health/get-next-best-actions",
        { params: { affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSchoolTimeline(
    affiliationId: number,
    params: { pageNumber?: number; pageSize?: number } = {},
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<SchoolTimelineEventDisplay>>(
        "aem-health/get-school-timeline",
        { params: { affiliationId, ...params } },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getActiveAlerts(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SchoolAlertDisplay[]>(
        "aem-health/get-active-alerts",
        { params: { affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async acknowledgeAlert(inputs: AcknowledgeAlertInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        "aem-health/acknowledge-alert",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── plan-§5.V–§5.AB — Academic Calendar & Monthly Plan ──────────────────

  async getAcademicCalendar(programId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AcademicCalendarEventDisplay[]>(
        "aem-health/get-academic-calendar",
        { params: { affiliationProgramId: programId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createAcademicCalendar(inputs: CreateAcademicCalendarInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AcademicCalendarEventDisplay[]>(
        "aem-health/create-academic-calendar",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Apply auto-fill to an existing calendar (backend computes the dates for empty, non-confirmed
  // events and returns the full updated list). D4/BR-B-018.
  async applyAcademicCalendarAutoFill(inputs: ApplyAutoFillInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AcademicCalendarEventDisplay[]>(
        "aem-health/apply-academic-calendar-auto-fill",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async confirmAcademicEventDate(inputs: ConfirmAcademicEventDateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<AcademicCalendarEventDisplay>(
        "aem-health/confirm-academic-event-date",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Add / edit / soft-remove calendar events (Id present = update, absent = add, IsActive:false = remove).
  async updateAcademicCalendar(inputs: UpdateAcademicCalendarInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<AcademicCalendarEventDisplay[]>(
        "aem-health/update-academic-calendar",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAutoFillPreview(programId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AutoFillPreviewDisplay>(
        "aem-health/get-auto-fill-preview",
        { params: { affiliationProgramId: programId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Holidays ────────────────────────────────────────────────────────────

  async getEffectiveHolidays(programId: number, year?: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<EffectiveHolidayMonthDisplay[]>(
        "aem-health/get-effective-holidays",
        { params: { affiliationProgramId: programId, year } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getHolidayOverrides(programId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<HolidayOverrideDisplay[]>(
        "aem-health/get-holiday-overrides",
        { params: { affiliationProgramId: programId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveHolidayOverrides(inputs: SaveHolidayOverridesInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<HolidayOverrideDisplay[]>(
        "aem-health/save-holiday-overrides",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveMonthlyPlan(inputs: SaveMonthlyPlanInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AffiliationMonthlyPlanDisplay[]>(
        "aem-health/save-monthly-plan",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getMonthlyPlan(programId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AffiliationMonthlyPlanDisplay[]>(
        "aem-health/get-monthly-plan",
        { params: { affiliationProgramId: programId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateProgramPlanUrls(inputs: UpdateProgramPlanUrlsInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        "aem-health/update-program-plan-urls",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── plan-§5.AC–§5.AO — Tickets ──────────────────────────────────────────

  async createTicket(inputs: SchoolTicketInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<SchoolTicketDisplay>(
        "am-tickets/create",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTicketsForAffiliation(params: {
    affiliationId: number;
    statusId?: number;
    departmentId?: number;
    // No server-side priority/category filters — am-tickets/get-for-affiliation only accepts
    // statusId/departmentId + paging (AR-6.6). Priority/category are filtered client-side.
    includeResolved?: boolean;
    pageNumber?: number;
    pageSize?: number;
  }): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<SchoolTicketSummaryDisplay>>(
        "am-tickets/get-for-affiliation",
        { params },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTicketsForUser(params: {
    statusId?: number;
    departmentId?: number;
    pageNumber?: number;
    pageSize?: number;
  }): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<SchoolTicketSummaryDisplay>>(
        "am-tickets/get-for-user",
        { params },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Role-scoped, paginated ticket list. The backend derives the scope from the JWT role
  // (Admin → all, Affiliation Manager → portfolio, others → own school); School/Status/
  // Category filtering happens client-side on the loaded page, so no filter params here.
  async getTicketsForScope(params: {
    pageNumber?: number;
    pageSize?: number;
  }): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<SchoolTicketSummaryDisplay>>(
        "am-tickets/get-for-scope",
        { params },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTicketById(ticketId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SchoolTicketDisplay>(
        `am-tickets/get-by-id/${ticketId}`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTicketStatus(inputs: UpdateTicketStatusInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<SchoolTicketSummaryDisplay>(
        "am-tickets/update-status",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addTicketMessage(inputs: AddTicketMessageInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<SchoolTicketDisplay>(
        "am-tickets/add-message",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async rateTicket(inputs: RateTicketInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<SchoolTicketSummaryDisplay>(
        "am-tickets/rate",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTicketCounts(affiliationId?: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<TicketCountsDisplay>("am-tickets/get-counts", {
        params: affiliationId ? { affiliationId } : undefined,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Role-scoped status counts for the badge rail (Admin → all, Affiliation Manager →
  // portfolio, others → own school). No params — scope comes from the JWT role.
  async getTicketCountsForScope(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<TicketCountsDisplay>(
        "am-tickets/get-counts-for-scope",
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDepartmentConfig(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<DepartmentEscalationConfigDisplay[]>(
        "am-tickets/get-department-config",
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateDepartmentConfig(inputs: DepartmentEscalationConfigInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<DepartmentEscalationConfigDisplay>(
        "am-tickets/update-department-config",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async closeTicket(inputs: CloseTicketInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<SchoolTicketSummaryDisplay>(
        "am-tickets/close",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async runLegacyMigration(dryRun: boolean = true): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<LegacyMigrationResultDisplay>(
        "am-tickets/run-legacy-migration",
        null,
        { params: { dryRun } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTicketUploadUrl(filename: string, contentType?: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AzureUploadUrl>("am-tickets/get-upload-url", {
        params: { filename, contentType },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── plan-§5.AP–§5.AW — Tasks ────────────────────────────────────────────

  async getTasks(params: {
    filter?: "today" | "this-week" | "overdue" | "completed";
    affiliationId?: number;
    taskTypeId?: number;
    userId?: number; // Admin only
    pageNumber?: number;
    pageSize?: number;
  }): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<AemTaskDisplay>>(
        "am-tasks/get-tasks",
        { params },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createTask(inputs: CreateAemTaskInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AemTaskDisplay>(
        "am-tasks/create-task",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async completeTask(inputs: CompleteAemTaskInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<AemTaskDisplay>(
        "am-tasks/complete-task",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async snoozeTask(inputs: SnoozeAemTaskInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<AemTaskDisplay>("am-tasks/snooze-task", inputs);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async cancelTask(inputs: CancelAemTaskInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<AemTaskDisplay>("am-tasks/cancel-task", inputs);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTaskCounts(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AemTaskCountsDisplay>("am-tasks/get-counts");
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTasksForAffiliation(
    affiliationId: number,
    params: { pageNumber?: number; pageSize?: number; includeCompleted?: boolean } = {},
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<AemTaskDisplay>>(
        "am-tasks/get-for-affiliation",
        { params: { affiliationId, ...params } },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async reassignTask(taskId: string, inputs: ReassignTaskInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.patch<AemTaskDisplay>(
        `am-tasks/${taskId}/reassign`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── plan-§5.AX–§5.BK — Visits ───────────────────────────────────────────

  async createVisit(inputs: CreateVisitInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<VisitDisplay>("am-visits/create", inputs);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getVisits(params: {
    affiliationId?: number;
    statusId?: number;
    fromDate?: string;
    toDate?: string;
    pageNumber?: number;
    pageSize?: number;
  }): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<VisitDisplay>>(
        "am-visits/list",
        { params },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTravelClusters(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<VisitTravelClusterDisplay[]>(
        "am-visits/travel-clusters",
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getVisitPreBriefData(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<VisitPreBriefDataDisplay>(
        `am-visits/pre-brief-data/${affiliationId}`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getVisit(visitId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<VisitDisplay>(`am-visits/${visitId}`);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateVisitPreBrief(
    visitId: string,
    inputs: UpdateVisitPreBriefInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<VisitDisplay>(
        `am-visits/${visitId}/pre-brief`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateInfraCheck(visitId: string, inputs: UpdateInfraCheckInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<VisitDisplay>(
        `am-visits/${visitId}/infra-check`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTeacherMeeting(
    visitId: string,
    inputs: UpdateTeacherMeetingInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<VisitDisplay>(
        `am-visits/${visitId}/teacher-meeting`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateClassroomObservation(
    visitId: string,
    inputs: UpdateClassroomObservationInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<VisitDisplay>(
        `am-visits/${visitId}/classroom-observation`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateStudentInteraction(
    visitId: string,
    inputs: UpdateStudentInteractionInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<VisitDisplay>(
        `am-visits/${visitId}/student-interaction`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateKdmClosing(visitId: string, inputs: UpdateKdmClosingInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<VisitDisplay>(
        `am-visits/${visitId}/kdm-closing`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateVisitStatus(visitId: string, inputs: UpdateVisitStatusInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.patch<VisitDisplay>(
        `am-visits/${visitId}/status`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async generateMom(visitId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<VisitDisplay>(
        `am-visits/${visitId}/generate-mom`,
        null,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async shareMom(visitId: string, inputs: ShareVisitMomInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<VisitDisplay>(
        `am-visits/${visitId}/share-mom`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── plan-§5.BL–§5.BQ — Assessments ─────────────────────────────────────

  async createAssessment(inputs: CreateAssessmentInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AssessmentDisplay>(
        "am-assessments/create",
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAssessments(params: {
    affiliationId: number;
    typeId?: number;
    statusId?: number;
    pageNumber?: number;
    pageSize?: number;
  }): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<AssessmentDisplay>>(
        "am-assessments/list",
        { params },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAssessmentSummary(affiliationId: number, academicYear: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AssessmentSummaryDisplay>(
        `am-assessments/summary/${affiliationId}`,
        { params: { academicYear } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAssessment(assessmentId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AssessmentDisplay>(
        `am-assessments/${assessmentId}`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async advanceAssessmentStatus(
    assessmentId: string,
    inputs: AdvanceAssessmentStatusInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.patch<AssessmentDisplay>(
        `am-assessments/${assessmentId}/status`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateWeakChapters(
    assessmentId: string,
    inputs: UpdateWeakChaptersInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<AssessmentDisplay>(
        `am-assessments/${assessmentId}/weak-chapters`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
